"use client"

import { useEffect, useRef, useState, type ReactNode } from "react"

/** A CSS media query, not visible copy, so it sits in a template literal. */
const REDUCED_MOTION = `(prefers-reduced-motion: reduce)`

/**
 * The section's one motion: the written recap slides out of the call window
 * once, the first time the section scrolls into view.
 *
 * The server render is the finished frame, so a reader with no script, a
 * reader who asked for reduced motion, and a reader who lands with the section
 * already on screen all see the recap in place and nothing moves. Only when the
 * section is still below the fold does the recap wait tucked into the window,
 * and it slides out when he reaches it. It never plays twice.
 */
export function RecapReveal({ className, children }: { className: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<"done" | "wait" | "go">("done")

  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === "undefined") return
    if (window.matchMedia(REDUCED_MOTION).matches) return
    let first = true
    const io = new IntersectionObserver(
      (entries) => {
        const inView = entries.some((entry) => entry.isIntersecting)
        if (first) {
          first = false
          if (inView) io.disconnect()
          else setState("wait")
          return
        }
        if (inView) {
          setState("go")
          io.disconnect()
        }
      },
      { threshold: 0.3 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div ref={ref} className={className} data-reveal={state}>
      {children}
    </div>
  )
}
